// Title-related constants

import { PRIMARY_NAV_ITEMS } from 'layouts/FirmBaseLayout/PrimaryNavigation/PrimaryNavigation.constants';
import { COMPANY_NAVIGATION_ROUTES } from 'pages/Company/companyNavigation';
import { FIRM_NAVIGATION_ROUTES } from 'pages/Firms/firmNavigation';

// Route patterns for matching
export const ROUTE_PATTERNS = {
  FIRMS: '/firms/',
  COMPANIES: '/companies/',
  USERS: '/users/',
  DASHBOARD: '/dashboard/',
  // Firm pages
  SETTINGS: '/settings',
  COMP_GROUPS: '/comp-groups',
  USER_MANAGEMENT: '/user-management',
  FIRM_WATERFALL: '/firm-waterfall',
} as const;

// Company route information derived from company navigation metadata.
export const COMPANY_ROUTE_MAP = COMPANY_NAVIGATION_ROUTES.reduce((acc, route) => {
  acc[route.slug] = {
    id: route.id,
    title: route.title,
    slug: route.slug,
    hidden: route.hidden,
  };
  return acc;
}, {} as Record<string, { id: string; title: string; slug: string; hidden: boolean }>);

// Firm route information derived from firm navigation metadata.
export const FIRM_ROUTE_MAP = FIRM_NAVIGATION_ROUTES.reduce((acc, route) => {
  acc[route.slug] = {
    id: route.id,
    title: route.title,
    slug: route.slug,
    hidden: route.hidden,
  };
  return acc;
}, {} as Record<string, { id: string; title: string; slug: string; hidden: boolean }>);

// Prism primary routes do not belong in the legacy firm navigation list,
// but the browser title resolver still needs their route metadata.
export const FIRM_PRIMARY_ROUTE_MAP = PRIMARY_NAV_ITEMS.reduce((acc, route) => {
  acc[route.slug] = {
    title: route.label,
    slug: route.slug,
  };
  return acc;
}, {} as Record<string, { title: string; slug: string }>);

// Get company route by slug
export const getCompanyRouteBySlug = (slug: string) => COMPANY_ROUTE_MAP[slug];

// Get company route title by slug
export const getCompanyRouteTitleBySlug = (slug: string) => COMPANY_ROUTE_MAP[slug]?.title || '';

// Get firm route by slug
export const getFirmRouteBySlug = (slug: string) => FIRM_ROUTE_MAP[slug];

// Get firm route title by slug
export const getFirmRouteTitleBySlug = (slug: string) =>
  FIRM_ROUTE_MAP[slug]?.title || FIRM_PRIMARY_ROUTE_MAP[slug]?.title || '';

// Page names mapping (display names) - only other pages, company and firm pages come from their respective route files
export const PAGE_NAMES = {
  // Other pages
  DASHBOARD: 'Dashboard',
  OVERVIEW: 'Scalar',
} as const;

// URL parameter names (matching the route patterns)
export const URL_PARAMS = {
  DATE: 'date',
  FIRM_SLUG: 'firmSlugParam',
  COMPANY_SLUG: 'companySlugParam',
  USER_ID: 'userIdParam',
  TABLE_SLUG: 'tableSlugParam',
} as const;

// Path segments (extracted from ROUTE_PATTERNS to avoid duplication)
export const PATH_SEGMENTS = {
  FIRMS: 'firms',
  COMPANIES: 'companies',
  USERS: 'users',
  DASHBOARD: 'dashboard',
} as const;


// Route type enums for non-company/firm routes (simplified)
export const ROUTE_TYPES = {
  // Other pages
  FIRM_SUMMARY: 'summary',
  DASHBOARD: 'dashboard',
  OVERVIEW: 'overview',
} as const;

export const COMPANY_ROUTE_SLUGS = COMPANY_NAVIGATION_ROUTES.map(route => route.slug);

export const FIRM_ROUTE_SLUGS = [
  ...FIRM_NAVIGATION_ROUTES.map(route => route.slug),
  ...PRIMARY_NAV_ITEMS.map(route => route.slug),
];

// Title configuration
export const TITLE_CONFIG = {
  DEFAULT_TITLE: 'Scalar',
  SEPARATOR: ' | ',
  DATE_FORMAT_OPTIONS: {
    year: 'numeric' as const,
    month: '2-digit' as const,
    day: '2-digit' as const,
  },
} as const;
